import { ArrowDropDown, ArrowRight } from '@mui/icons-material'
import { Box, PaginationItem, useMediaQuery, useTheme } from '@mui/material'
import {
  DataGridProProps,
  GridCallbackDetails,
  GridCellParams,
  GridColumnOrderChangeParams,
  GridEventListener,
  GridInitialState,
  GridInputSelectionModel,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowId,
  GridRowOrderChangeParams,
  GridRowParams,
  GridSelectionModel,
  GridSlotsComponent,
  GridSortModel,
  GridToolbar,
} from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React, { ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogContextType } from 'src/enums'
import { QueryKeyT, useGet } from '../../api/reactQuery'
import { QuickFilterBounceTime } from '../../constant'
import { IBaseEntity } from '../../generated-types'
import { PaginatedResultList } from '../../generated-types/paginated-result-list'
import { Spinner } from '../Spinner'
import {
  COLUMN_HEADER_GAP,
  MINIFIED_COLUMN_HEADER_HEIGHT,
  PaginationComponent,
  StyledDataGridPro,
} from './GenericTable.style'
import MobileCardList from './MobileCardList'
import { NextArrow, PreviousArrow } from './PaginationArrows'

interface ICustomDataGridWithPagination extends Omit<DataGridProProps, 'rows'> {
  queryUrl: string
  queryParams?: object
  enablePagination: boolean
  columns: any[]
  initialPage: number
  initialState?: GridInitialState
  pageOptions: number[]
  pageSize: number
  setPageSize: (pageSize: number) => void
  onCellClick?: GridEventListener<'cellClick'> | undefined
  noRowsMessage?: string
  isRowHightAuto?: boolean
  onSelectionModelChange?: ((selectionModel: GridSelectionModel, details: GridCallbackDetails<any>) => void) | undefined
  onRowClick?: GridEventListener<'rowClick'> | undefined
  onCellEditCommit?: GridEventListener<'cellEditCommit'> | undefined
  isCellEditable?: ((params: GridCellParams<any, any, any>) => boolean) | undefined
  selectionModel?: GridInputSelectionModel | undefined
  isRowSelectable?: (params: GridRowParams<any>) => boolean
  getRowHeight?: ((params: GridRowHeightParams) => GridRowHeightReturnValue) | undefined
  apiRef?: React.MutableRefObject<GridApiPro>
  loading?: boolean
  enableFilter?: boolean
  disableSelectionOnClick?: boolean
  sortModel?: GridSortModel | undefined
  enableQuickFilter?: boolean
  getRowClassName?: (params: GridRowClassNameParams<IBaseEntity>) => string
  getDetailPanelContent?: ((params: GridRowParams<any>) => React.ReactNode) | undefined
  getDetailPanelHeight?: ((params: GridRowParams<any>) => number | 'auto') | undefined
  enablePaginationButtons?: {
    showFirstButton?: boolean
    showLastButton?: boolean
  }
  onDetailPanelExpandedRowIdsChange?: ((ids: GridRowId[], details: GridCallbackDetails<any>) => void) | undefined
  detailPanelExpandedRowIds?: GridRowId[] | undefined
  generateQueryKey?: (page: number, pageSize: number, fields?: string) => QueryKeyT | undefined
  hideDefaultFooterPagination?: boolean
  hideColumnHeaders?: boolean
  customStyles?: any
  hideFooterSelectedRowCount?: boolean
  rowTopSpacing?: number
  rowBottomSpacing?: number
  components?: Partial<GridSlotsComponent> | undefined
  className?: any
  mobileViewCard?: (row: any) => ReactElement
  height?: number | string
  autoHeight?: boolean
  fitRowsHeight?: boolean
  isViewAllRecords?: boolean
  isMinifiedColumnHeader?: boolean
  noDataComponent?: React.JSXElementConstructor<any> | undefined
  contextType?: DialogContextType
  maxHeight?: number | string
  enableRowReordering?: boolean
  onRowOrderChange?: (params: GridRowOrderChangeParams) => void
  onColumnOrderChange?: (params: GridColumnOrderChangeParams) => void
  showHeaderOnEmptyState?: boolean
  columnOptions?: string[]
  allowHorizontalScroll?: boolean
  checkboxSelection?: boolean
}
export const CustomDataGridWithPagination: React.FC<ICustomDataGridWithPagination> = memo(
  ({
    queryUrl,
    queryParams,
    enablePagination,
    columns,
    initialPage,
    pageOptions,
    pageSize,
    setPageSize,
    onCellClick,
    noRowsMessage,
    initialState,
    isRowHightAuto = false,
    children,
    onSelectionModelChange,
    onRowClick,
    onCellEditCommit,
    isRowSelectable,
    getRowHeight,
    apiRef,
    loading = false,
    enableFilter = false,
    disableSelectionOnClick,
    sortModel,
    enableQuickFilter = false,
    getDetailPanelHeight,
    getDetailPanelContent,
    enablePaginationButtons,
    detailPanelExpandedRowIds,
    onDetailPanelExpandedRowIdsChange,
    generateQueryKey,
    hideDefaultFooterPagination,
    hideColumnHeaders,
    customStyles,
    hideFooterSelectedRowCount,
    rowTopSpacing,
    rowBottomSpacing,
    components,
    mobileViewCard,
    className,
    height,
    autoHeight = true,
    fitRowsHeight = false,
    getRowClassName,
    isViewAllRecords,
    isMinifiedColumnHeader,
    noDataComponent,
    contextType,
    maxHeight,
    enableRowReordering = false,
    onRowOrderChange,
    onColumnOrderChange,
    showHeaderOnEmptyState = false,
    columnOptions,
    allowHorizontalScroll = false,
    checkboxSelection,
    ...props
  }) => {
    const [page, setPage] = useState(initialPage ?? 0)
    const [defaultPageSize, setDefaultPageSize] = useState(pageSize)
    const { t } = useTranslation(['common'])
    const theme = useTheme()
    const isSmall = useMediaQuery<any>(theme.breakpoints.down('sm'))

    useEffect(() => {
      setDefaultPageSize(pageSize)
    }, [pageSize])

    let parsedProps = { ...props }

    if (isRowHightAuto) {
      parsedProps = {
        ...props,
      }
    }

    const {
      data: serverData,
      isLoading,
      refetch,
    } = useGet<PaginatedResultList<IBaseEntity>>(
      `${queryUrl}`,
      {
        page,
        pageSize: defaultPageSize,
        fields: 'Id,' + columnOptions?.join(','),
        ...queryParams,
      },
      { queryKey: generateQueryKey && generateQueryKey(page, defaultPageSize, columnOptions?.join(',')) },
    )

    useEffect(() => {
      if (
        serverData?.totalCount &&
        serverData?.totalCount != defaultPageSize &&
        serverData?.totalCount > defaultPageSize &&
        ((height && !enablePagination && !autoHeight) || isViewAllRecords)
      ) {
        setDefaultPageSize(serverData.totalCount)
      }
    }, [serverData?.totalCount, autoHeight, enablePagination, height])

    useEffect(() => {
      refetch()
    }, [defaultPageSize])

    // Reset to first page when query params (filters) change
    const queryParamsKey = JSON.stringify(queryParams)
    useEffect(() => {
      setPage(0)
    }, [queryParamsKey])

    const handlePageChange = (_, newPage) => {
      setPage(newPage)
    }

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(event.target.value, 10))
      setPage(0)
    }, [])

    const shouldFitRowsHeight = useMemo(() => {
      if (!fitRowsHeight || autoHeight || typeof height !== 'number' || !serverData?.items) return false
      const rowHeightValue = getRowHeight?.({} as GridRowHeightParams)
      const resolvedRowHeight =
        typeof rowHeightValue === 'number'
          ? rowHeightValue
          : isMinifiedColumnHeader
          ? MINIFIED_COLUMN_HEADER_HEIGHT
          : 52
      const headerHeight = hideColumnHeaders
        ? 0
        : (isMinifiedColumnHeader ? MINIFIED_COLUMN_HEADER_HEIGHT : 56) + COLUMN_HEADER_GAP
      const footerHeight = enablePagination && !hideDefaultFooterPagination ? 52 : 0
      const estimatedContentHeight = headerHeight + serverData.items.length * resolvedRowHeight + footerHeight
      return estimatedContentHeight <= height
    }, [
      fitRowsHeight,
      autoHeight,
      height,
      serverData?.items,
      getRowHeight,
      isMinifiedColumnHeader,
      hideColumnHeaders,
      enablePagination,
      hideDefaultFooterPagination,
    ])

    return (
      <>
        <Box
          sx={{
            height: shouldFitRowsHeight ? 'auto' : height,
            maxHeight,
            width: '100%',
            overflow: allowHorizontalScroll ? 'auto !important' : 'hidden !important',
          }}
        >
          {serverData?.items && serverData?.items?.length == 0 && noDataComponent ? (
            !showHeaderOnEmptyState ? (
              React.createElement(noDataComponent)
            ) : (
              <></>
            )
          ) : isSmall ? (
            <MobileCardList
              columns={columns}
              rows={serverData?.items ?? []}
              mobileViewCard={mobileViewCard}
              getDetailPanelContent={getDetailPanelContent}
              onRowClick={onRowClick}
              onCellClick={onCellClick}
              contextType={contextType}
              getRowId={props.getRowId}
            />
          ) : (
            <StyledDataGridPro
              id="custom-data-grid"
              columns={columns}
              rows={serverData?.items ?? []}
              rowCount={serverData?.totalCount ?? 0}
              autoHeight={autoHeight || shouldFitRowsHeight}
              disableColumnFilter={!enableFilter}
              disableDensitySelector
              disableMultipleColumnsSorting
              disableColumnMenu
              disableMultipleSelection
              disableColumnSelector
              disableColumnResize
              loading={isLoading}
              hideFooter={!enablePagination || hideDefaultFooterPagination}
              pagination={enablePagination}
              rowsPerPageOptions={pageOptions}
              pageSize={defaultPageSize}
              page={page}
              onPageChange={((page: number) => setPage(page)) as any}
              onPageSizeChange={((pageSize: number) => setPageSize(pageSize)) as any}
              getRowClassName={Params => (getRowClassName ? getRowClassName(Params) : 'bg-white ')}
              initialState={initialState}
              onCellDoubleClick={onCellClick}
              onRowClick={onRowClick}
              components={{
                ...components,
                Toolbar: GridToolbar,
                NoResultsOverlay:
                  noDataComponent ??
                  (() => (
                    <Box className="font-semibold text-center text-gray-600 text-l">
                      {noRowsMessage ?? t('noResultFound')}
                    </Box>
                  )),
                NoRowsOverlay:
                  noDataComponent ??
                  (() => (
                    <Box
                      className="font-semibold text-center text-gray-600 text-l"
                      display={'flex'}
                      flexDirection={'column'}
                      alignItems={'center'}
                      p={6}
                    >
                      {noRowsMessage ?? t('noResultFound')}
                    </Box>
                  )),
                LoadingOverlay: () => {
                  return (
                    <Box
                      height={'100%'}
                      width={'100%'}
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                    >
                      <Spinner />
                    </Box>
                  )
                },
                DetailPanelExpandIcon: ArrowRight,
                DetailPanelCollapseIcon: ArrowDropDown,
              }}
              componentsProps={{
                pagination: {
                  count: serverData?.totalCount ?? 0,
                  page: page,
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handleChangeRowsPerPage,
                  rowsPerPage: defaultPageSize,
                  rowsPerPageOptions: pageOptions,
                  showFirstButton: enablePaginationButtons?.showFirstButton ?? false,
                  showLastButton: enablePaginationButtons?.showLastButton ?? false,
                },
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                  showQuickFilter: enableQuickFilter,
                  quickFilterProps: { debounceMs: QuickFilterBounceTime },
                },
              }}
              getRowSpacing={_ => {
                return {
                  top: rowTopSpacing ?? 0,
                  bottom: rowBottomSpacing ?? 0,
                }
              }}
              paginationMode="server"
              allowHorizontalScroll={allowHorizontalScroll}
              {...parsedProps}
              className={`${customStyles ?? ''} ${className ?? ''} scrollbar`}
              onSelectionModelChange={onSelectionModelChange}
              onCellEditCommit={onCellEditCommit}
              onCellClick={onCellClick}
              isRowSelectable={isRowSelectable}
              getRowHeight={getRowHeight}
              apiRef={apiRef}
              disableSelectionOnClick={disableSelectionOnClick}
              sortModel={sortModel}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              detailPanelExpandedRowIds={detailPanelExpandedRowIds}
              onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
              hideFooterPagination={hideDefaultFooterPagination}
              hideFooterSelectedRowCount={hideFooterSelectedRowCount}
              hideColumnHeaders={hideColumnHeaders}
              isMinifiedColumnHeader={isMinifiedColumnHeader}
              headerHeight={isMinifiedColumnHeader ? 35 : undefined}
              {...props}
              enableRowReordering={enableRowReordering}
              onRowOrderChange={onRowOrderChange}
              onColumnOrderChange={onColumnOrderChange}
              checkboxSelection={checkboxSelection}
            />
          )}
        </Box>
        {serverData && enablePagination && (hideDefaultFooterPagination || isSmall) && serverData.items.length != 0 && (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'} padding={'8px'} paddingTop={0}>
            <PaginationComponent
              size="small"
              count={(serverData && Math.ceil(serverData?.totalCount / defaultPageSize)) ?? 0}
              page={page + 1}
              onChange={(event, page) => handlePageChange(event, page - 1)}
              renderItem={item => (
                <PaginationItem components={{ next: NextArrow, previous: PreviousArrow }} {...item} />
              )}
            />
          </Box>
        )}
      </>
    )
  },
)
